// FILE: MemoriesClient.ts
// Purpose: HTTP wrapper around `/api/admin/memories*`, mirroring AgentsClient's
//          shape (plain object, static methods) over the shared `adminFetch`.
// Layer: Web client
// Exports: `MemoriesClient`

import type {
  AdminMemoriesResponse,
  AdminMemoryCreateRequest,
  AdminMemoryIndexPreview,
  AdminMemoryResponse,
  AdminMemoryUpdateRequest,
  AgentMemory,
  AgentMemoryId,
  AgentMemoryScope,
  ManagedAgentId,
  ProjectId,
  UserId,
} from "@taskin-code/contracts";

import { adminFetch } from "../lib/adminFetch";

export const MemoriesClient = {
  /**
   * One scope's memories, whatever their status.
   *
   * Omitting `userId` on the `user` scope is not an oversight — the server
   * reads it as "mine", which is what lets a person without `m.view` open their
   * own tab. Passing one is the administrator's read.
   */
  async list(input: {
    readonly scope: AgentMemoryScope;
    readonly projectId?: ProjectId | null;
    readonly userId?: UserId | null;
  }): Promise<ReadonlyArray<AgentMemory>> {
    const params = new URLSearchParams({ scope: input.scope });
    if (input.projectId) params.set("projectId", input.projectId);
    if (input.userId) params.set("userId", input.userId);
    const result = await adminFetch<AdminMemoriesResponse>(
      `/api/admin/memories?${params.toString()}`,
    );
    return result.memories;
  },

  /** The approval queue: what agents proposed and nobody has ruled on yet. */
  async listProposals(): Promise<ReadonlyArray<AgentMemory>> {
    const result = await adminFetch<AdminMemoriesResponse>("/api/admin/memories/proposals");
    return result.memories;
  },

  /** What a thread in this project, opened by this person, would carry. */
  async indexPreview(input: {
    readonly projectId?: ProjectId | null;
    readonly userId?: UserId | null;
    /** Naming an agent turns "what exists here" into "what would this load". */
    readonly agentId?: ManagedAgentId | null;
    readonly strict?: boolean;
  }): Promise<AdminMemoryIndexPreview> {
    const params = new URLSearchParams();
    if (input.projectId) params.set("projectId", input.projectId);
    if (input.userId) params.set("userId", input.userId);
    if (input.agentId) params.set("agentId", input.agentId);
    if (input.strict === true) params.set("strict", "1");
    const query = params.toString();
    return await adminFetch<AdminMemoryIndexPreview>(
      `/api/admin/memories/index-preview${query === "" ? "" : `?${query}`}`,
    );
  },

  async create(input: AdminMemoryCreateRequest): Promise<AgentMemory> {
    const result = await adminFetch<AdminMemoryResponse>("/api/admin/memories", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return result.memory;
  },

  async update(memoryId: AgentMemoryId, patch: AdminMemoryUpdateRequest): Promise<AgentMemory> {
    const result = await adminFetch<AdminMemoryResponse>(`/api/admin/memories/${memoryId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    return result.memory;
  },

  /** Accept a proposal, or bring an archived memory back into the index. */
  async approve(memoryId: AgentMemoryId): Promise<AgentMemory> {
    const result = await adminFetch<AdminMemoryResponse>(
      `/api/admin/memories/${memoryId}/approve`,
      { method: "POST" },
    );
    return result.memory;
  },

  /** Retire a memory. It stops steering anything and stays readable for audit. */
  async archive(memoryId: AgentMemoryId): Promise<void> {
    await adminFetch<void>(`/api/admin/memories/${memoryId}`, { method: "DELETE" });
  },
};
